'use client';

import { useMemo } from 'react';
import { useGetCodeByNameQuery } from 'src/api/redux';
import type {
  CodeData,
  CriteriaInputs,
  StrategyDetails,
  StrategyCodeType,
} from 'src/components/Strategy/shared/types';

interface UseCodeDataMapParams {
  criteriaInputs: CriteriaInputs;
  strategyDetails?: StrategyDetails;
  strategyType?: StrategyCodeType | string;
}

/**
 * Hook to build a map of code IDs to their metadata
 * Consolidates code data from criteriaInputs and fetched API data
 */
export const useCodeDataMap = ({
  criteriaInputs,
  strategyDetails,
  strategyType,
}: UseCodeDataMapParams): Map<string, CodeData> => {
  // Extract code IDs that need fetching (for FRESH_FACES from code_set.filters)
  const codeIdsToFetch = useMemo(() => {
    if (strategyType !== 'FRESH_FACES' || !strategyDetails?.code_set?.filters) {
      return [];
    }

    const codeIds: string[] = [];
    strategyDetails.code_set.filters.forEach((filter) => {
      if (filter.values && Array.isArray(filter.values)) {
        codeIds.push(...filter.values);
      }
    });

    return codeIds;
  }, [strategyType, strategyDetails]);

  const { data: fetchedCodeData } = useGetCodeByNameQuery(
    { codeIds: codeIdsToFetch },
    { skip: codeIdsToFetch.length === 0 }
  );

  // Build the code data map from both criteriaInputs and fetched data
  const codeDataMap = useMemo(() => {
    const map = new Map<string, CodeData>();

    // Extract from criteriaInputs ui_metadata
    Object.values(criteriaInputs).forEach((input) => {
      if (input.type === 'codes' && input.ui_metadata) {
        (input.ui_metadata as CodeData[]).forEach((codeData) => {
          const codeId = codeData.id || codeData.code_name;
          if (codeId) {
            map.set(codeId, codeData);
          }
        });
      }
    });

    // Merge fetched code data
    if (fetchedCodeData?.results) {
      (fetchedCodeData.results as CodeData[]).forEach((codeData: CodeData) => {
        const codeId = codeData.id || codeData.code_name;
        if (codeId) {
          map.set(codeId, codeData);
        }
      });
    }

    return map;
  }, [criteriaInputs, fetchedCodeData]);

  return codeDataMap;
};

export default useCodeDataMap;
